import type { CSSProperties } from 'react'
import { getIcon } from '@/components/BaseData/appearance/icons/getIcon'
import { kebabToPascal } from '@/components/BaseData/appearance/icons/slug'
import { getInitials } from '@/lib/format/initials'
import { cn } from '@/lib/utils'

type AvatarSize = 'sm' | 'md' | 'lg'

interface UserAvatarProps {
  name?: string | null
  /** Slug lucide KEBAB; null/desconhecido → iniciais (NAO um icone default). */
  icon?: string | null
  /** Hex #RRGGBB; null → gradiente primary (o look de hoje do rail). */
  color?: string | null
  size?: AvatarSize
  className?: string
}

const BOX: Record<AvatarSize, string> = {
  sm: 'size-8 rounded-lg text-[10px]',
  md: 'size-12 rounded-xl text-sm',
  lg: 'size-16 rounded-2xl text-xl',
}

const ICON: Record<AvatarSize, string> = {
  sm: 'size-4',
  md: 'size-6',
  lg: 'size-8',
}

/** Sufixos hex-alpha deterministicos (testaveis em jsdom, sem color-mix). */
const BG_ALPHA = '26' // 15%
const BORDER_ALPHA = '59' // 35%

/**
 * Avatar do usuario logado: mesmo par icone+cor de `ChannelAvatar`.
 *
 * Diferenca deliberada: NAO ha icone default. Sem `icon` a saida e TEXTO (as
 * iniciais) — herdar o `Antenna` do ChannelAvatar poria uma antena no avatar de
 * uma pessoa.
 */
export function UserAvatar({ name, icon, color, size = 'md', className }: UserAvatarProps) {
  const Icon = icon ? getIcon(kebabToPascal(icon)) : undefined
  const tinted: CSSProperties | undefined = color
    ? { color, backgroundColor: `${color}${BG_ALPHA}`, borderColor: `${color}${BORDER_ALPHA}` }
    : undefined

  return (
    <div
      aria-hidden="true"
      style={tinted}
      className={cn(
        'flex shrink-0 select-none items-center justify-center border font-semibold tracking-wide',
        BOX[size],
        !color && 'border-white/10 bg-gradient-to-br from-primary to-purple-500 text-white',
        className,
      )}
    >
      {Icon ? <Icon className={ICON[size]} /> : getInitials(name) || '?'}
    </div>
  )
}
